import type { LucideIcon } from 'lucide-react';
import { Server, User } from 'lucide-react';

import { urls } from '@app/urls';
import type { Asset, IPDetails } from '@features/investigations/api/files.types';
import type { FacetFilter } from '@shared/api/findings/findings-api.types';
import { FacetedFilterOperator } from '@shared/api/findings/findings.types';
import type {
  FacetedFilter,
  FilterFieldValue,
  FilterPill,
  ParsedFilters,
  SetFilterFormValue,
} from '@shared/api/findings/findings.types';
import { toHumanReadableLabel, UI_LABELS } from '@shared/utils/label.utils';

import { getFiltersZeroCount } from './trace-findings.helpers';

export const EVENT_FIELD_DEFS = {
  USERNAME: {
    key: 'Username',
    labels: UI_LABELS.USERNAME,
    isActivityField: true,
  },
  HOSTNAME: {
    key: 'Source host',
    labels: UI_LABELS.HOSTNAME,
    isActivityField: true,
  },
  IP_ADDRESS: {
    key: 'IP Address',
    labels: UI_LABELS.IP_ADDRESS,
    isActivityField: true,
  },
  EVENT_ID: {
    key: 'event_id',
    labels: UI_LABELS.EVENT_ID,
    isActivityField: false,
  },
  MESSAGE: {
    key: 'message',
    labels: UI_LABELS.MESSAGE,
    isActivityField: false,
  },
  EVENT_TIMESTAMP: {
    key: 'event_timestamp',
    labels: UI_LABELS.EVENT_TIMESTAMP,
    isActivityField: false,
  },
} as const;

interface TargetItem {
  type: string;
  value: string;
  icon: LucideIcon;
}

export function cleanFieldValuesForSubmission(data: {
  selectedFields: string[];
  fieldValues: Record<string, FilterFieldValue>;
}): Record<string, FilterFieldValue> {
  const cleanedFieldValues: Record<string, FilterFieldValue> = {};
  for (const key of data.selectedFields) {
    const fv = data.fieldValues[key];
    if (fv?.values && fv.values.length > 0) {
      cleanedFieldValues[key] = {
        operator: fv.operator || FacetedFilterOperator.IS,
        values: [...fv.values],
      };
    }
  }
  return cleanedFieldValues;
}

export function normalizeFiltersForUI(filters: FacetedFilter[]): FacetedFilter[] {
  return filters.map((f) => {
    let operator: FacetedFilterOperator;
    if (f.operator === FacetedFilterOperator.IS) operator = FacetedFilterOperator.IS;
    else if (f.operator === FacetedFilterOperator.NOT) operator = FacetedFilterOperator.NOT;
    else operator = FacetedFilterOperator.IS;
    return {
      ...f,
      key: f.key,
      label: toHumanReadableLabel(f.label),
      operator,
      options: f.options.map((opt) => ({ ...opt })),
    };
  });
}

export function serializeFiltersForApi(filtersQuery: Record<string, FilterFieldValue>): { filters: FacetFilter[] } {
  return {
    filters: Object.entries(filtersQuery).map(([key, value]) => ({
      key,
      operation: value?.operator === FacetedFilterOperator.NOT ? FacetedFilterOperator.NOT : FacetedFilterOperator.IS,
      values: value?.values ?? [],
    })),
  };
}

export function getDisplayLabel(label: string | undefined | null): string {
  if (label === undefined || label === null || label.trim() === '') return 'empty value';
  return label;
}

export function getPillsFromFilters(filters: ParsedFilters): FilterPill[] {
  if (!filters || typeof filters !== 'object') return [];
  const pills: FilterPill[] = [];
  Object.entries(filters).forEach(([field, filter]) => {
    if (filter && Array.isArray(filter.values)) {
      for (const value of filter.values) {
        pills.push({ field, operator: filter.operator, value });
      }
    }
  });
  return pills;
}

export function cloneParsedFiltersSafe(filters: ParsedFilters): ParsedFilters {
  return Object.fromEntries(
    Object.entries(filters ?? {}).map(([k, v]) => [
      k,
      v ? { ...v, values: Array.isArray(v.values) ? [...v.values] : [] } : v,
    ]),
  );
}

export function toggleFieldHelper({
  field,
  getValues,
  setValue,
  filtersData,
  filterSearch,
  updateFiltersFromForm,
}: {
  field: string;
  getValues: (name?: 'selectedFields' | 'fieldValues') => string[] | Record<string, FilterFieldValue>;
  setValue: SetFilterFormValue;
  filtersData: FacetedFilter[];
  filterSearch: string;
  updateFiltersFromForm: () => void;
}) {
  const current = getValues('selectedFields') as string[];
  const filtersZeroCount = getFiltersZeroCount(filtersData, filterSearch);
  const zeroCountKeys = new Set<string>(filtersZeroCount.map((f) => f.key));

  if (current.includes(field)) {
    setValue(
      'selectedFields',
      current.filter((x: string) => x !== field),
    );
    setValue(`fieldValues.${field}.addedWithZeroValues`, undefined);
  } else {
    setValue('selectedFields', [...current, field]);
    const fieldValues = getValues('fieldValues') as Record<string, FilterFieldValue>;
    if (!fieldValues[field]?.operator) {
      setValue(`fieldValues.${field}.operator`, FacetedFilterOperator.IS);
    }
    if (zeroCountKeys.has(field)) {
      setValue(`fieldValues.${field}.addedWithZeroValues`, true);
    } else {
      setValue(`fieldValues.${field}.addedWithZeroValues`, undefined);
    }
  }
  updateFiltersFromForm();
}

export function allFieldsHaveValue(selectedFields: string[], fieldValues: Record<string, FilterFieldValue>): boolean {
  if (selectedFields.length === 0) return false;
  return selectedFields.every(
    (fieldKey) => Array.isArray(fieldValues?.[fieldKey]?.values) && (fieldValues?.[fieldKey]?.values?.length ?? 0) > 0,
  );
}

export function filtersHaveChanged(
  currentFilters: Record<string, FilterFieldValue>,
  initialFilters: Record<string, FilterFieldValue>,
): boolean {
  const currentKeys = Object.keys(currentFilters).sort((a, b) => a.localeCompare(b));
  const initialKeys = Object.keys(initialFilters).sort((a, b) => a.localeCompare(b));

  if (currentKeys.length !== initialKeys.length) return true;

  if (currentKeys.join(',') !== initialKeys.join(',')) return true;

  for (const key of currentKeys) {
    const current = currentFilters[key];
    const initial = initialFilters[key];

    if (current?.operator !== initial?.operator) return true;

    const currentValues = (current?.values ?? []).slice().sort((a, b) => a.localeCompare(b));
    const initialValues = (initial?.values ?? []).slice().sort((a, b) => a.localeCompare(b));

    if (currentValues.length !== initialValues.length) return true;
    if (currentValues.join('|') !== initialValues.join('|')) return true;
  }

  return false;
}

export interface ApplyDisabledArgs {
  isFetching: boolean;
  hasChanges: boolean;
  selectedFields: string[];
  allFieldsHaveValue: boolean;
}

export function isApplyDisabled({
  isFetching,
  hasChanges,
  selectedFields,
  allFieldsHaveValue,
}: ApplyDisabledArgs): boolean {
  if (isFetching) return true;
  if (!hasChanges) return true;
  return selectedFields.length > 0 && !allFieldsHaveValue;
}

export function totalSelectedValues(filtersQuery: ParsedFilters): number {
  return Object.values(filtersQuery)
    .map((fv) => (Array.isArray(fv?.values) ? fv.values.length : 0))
    .reduce((sum, count) => sum + count, 0);
}

export function resetFilters({
  setFilters,
  reset,
}: {
  setFilters: (filters: Record<string, FilterFieldValue>) => void;
  reset: (values: { selectedFields: string[]; fieldValues: Record<string, FilterFieldValue> }) => void;
}) {
  const url = new URL(globalThis.location.href);
  const pathname = url.pathname;

  const hexDigit = '[0-9a-f]';
  const uuidPattern = `${hexDigit}{8}-${hexDigit}{4}-${hexDigit}{4}-${hexDigit}{4}-${hexDigit}{12}`;
  const insightHashPattern = `${hexDigit}{32}`;

  const investigationPattern = new RegExp(`^${urls.results.path}/${uuidPattern}$`, 'i');
  const insightPattern = new RegExp(`^${urls.results.path}/${uuidPattern}/insight/${insightHashPattern}$`, 'i');

  const isValidPath = investigationPattern.test(pathname) || insightPattern.test(pathname);
  const safePath = isValidPath ? pathname : encodeURI(pathname);

  /** This might look excessive, but I added both Regex validation and passing through the URL API
   * in an attempt to appease Veracode, which was flagging the URL manipulation as a potential XSS vulnerability.
   * The regex ensures that the path matches expected patterns for investigation and insight URLs,
   * while the URL API provides an additional layer of encoding
   * to prevent any malicious input from being executed. */

  globalThis.history.replaceState(null, '', safePath);

  setFilters({});
  reset({ selectedFields: [], fieldValues: {} });
}

export function getTargetsFromInvestigation(investigation: {
  data?: { scope?: { userName?: string | null; systemName?: string | null; ipAddress?: string | null } };
}): TargetItem[] {
  const { scope } = investigation?.data ?? {};
  const targets: TargetItem[] = [];

  if (scope?.userName) {
    const userValues = scope.userName
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    targets.push({
      type: userValues.length > 1 ? UI_LABELS.USERNAME.plural : UI_LABELS.USERNAME.singular,
      value: scope.userName,
      icon: User,
    });
  }
  if (scope?.systemName) {
    targets.push({ type: UI_LABELS.HOSTNAME.singular, value: scope.systemName, icon: Server });
  }
  if (scope?.ipAddress) {
    targets.push({ type: UI_LABELS.IP_ADDRESS.singular, value: scope.ipAddress, icon: Server });
  }
  return targets;
}

const isEventSource = (value: unknown): value is Record<string, IPDetails> => {
  if (typeof value !== 'object' || value === null) return false;

  return Object.values(value).every((v) => v !== null && typeof v === 'object' && 'status' in v && 'rowCount' in v);
};

// Returns the total number of active Application Instances across all assets.
// An active source has an entry with status === 'success'
export const getTotalActiveSources = (assets: [string, Asset][]) =>
  assets.flatMap(([, asset]) =>
    Object.values(asset).filter(
      (value): value is Record<string, IPDetails> =>
        isEventSource(value) && Object.values(value).some((d) => d.status === 'success'),
    ),
  ).length;

export const getSources = (asset: Asset) => {
  return Object.entries(asset).filter(([key, value]) => key !== 'assetHostName' && value && typeof value === 'object');
};

export const getSourcesActiveCount = (sources: [string, string | Record<string, IPDetails>][]) => {
  return sources.filter(
    ([, value]) =>
      typeof value === 'object' && value !== null && Object.values(value).some((v) => v.status === 'success'),
  ).length;
};

const FILTER_MODAL_TRIGGER_GAP = 16;

export function getFilterModalPayload(
  triggerElement: HTMLButtonElement | null,
  investigationId: string | undefined,
): {
  triggerTop?: number;
  triggerLeft?: number;
  triggerWidth?: number;
  investigationId: string | undefined;
} {
  if (triggerElement) {
    const rect = triggerElement.getBoundingClientRect();
    return {
      triggerTop: rect.top,
      triggerLeft: rect.right + FILTER_MODAL_TRIGGER_GAP,
      triggerWidth: 0,
      investigationId,
    };
  }

  return { investigationId };
}
